import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Table,
  message,
  Card,
} from "antd";
import { useParams, useNavigate } from "react-router-dom";


import api from "../services/api";

export default function Start() {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);

  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);

  const [teams, setTeams] = useState([]);
  const [teamA, setTeamA] = useState(null);
  const [teamB, setTeamB] = useState(null);

  const [players, setPlayers] = useState([]); // all available players
  const [teamPlayersMap, setTeamPlayersMap] = useState({}); // key = teamId, value = array of players
  

  const [selectedTeamForPlayer, setSelectedTeamForPlayer] = useState(null);

  // Modal states
  const [isTournamentModalOpen, setIsTournamentModalOpen] = useState(false);
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [playerModalOpen, setPlayerModalOpen] = useState(false);

  // Forms
  const [tournamentForm] = Form.useForm();
  const [matchForm] = Form.useForm();
  const [teamForm] = Form.useForm();
  const [playerForm] = Form.useForm();

  const navigate = useNavigate();

  // =================== LOAD DATA ===================
  useEffect(() => {
    loadTournaments();
    loadTeams();
    loadPlayers();
  }, []);

  const loadTournaments = async () => {
    try {
      const res = await api.get("/tournaments");
      setTournaments(res.data.data || []);
    } catch {
      message.error("Failed to load tournaments");
    }
  };

  const loadMatches = async (tournamentId) => {
    setLoadingMatches(true);
    try {
      const res = await api.get("/matches", { params: { tournament_id: tournamentId } });
      setMatches(res.data.data || []);
    } catch {
      message.error("Failed to load matches");
    }
    setLoadingMatches(false);
  };

  const loadTeams = async () => {
    try {
      const res = await api.get("/teams");
      setTeams(res.data.data || []);
    } catch {
      message.error("Failed to load teams");
    }
  };

  const loadPlayers = async () => {
    try {
      const res = await api.get("/players");
      setPlayers(res.data.data || []);
    } catch {
      message.error("Failed to load players");
    }
  };

 const loadTeamPlayers = async (teamId) => {
  try {
    const res = await api.get("/team-players", { params: { team_id: teamId } });
    setTeamPlayersMap(prev => ({ ...prev, [teamId]: res.data.data || [] }));
  } catch {
    message.error("Failed to load team players");
  }
};


  const loadAssignedTeams = async (matchId) => {
    try {
      const res = await api.get(`/matchteams/${matchId}`, { params: { match_id: matchId } });
      const assigned = res.data.data || [];

      const t1 = assigned.find((t) => t.role === "team1");
      const t2 = assigned.find((t) => t.role === "team2");

      setTeamA(t1 ? t1.team_id : null);
      setTeamB(t2 ? t2.team_id : null);
    } catch {
      message.error("Failed to load assigned teams");
    }
  };

  // =================== CREATE HANDLERS ===================
  const createTournament = async (values) => {
    try {
      const payload = {
        ...values,
        start_date: values.start_date.format("YYYY-MM-DD"),
        end_date: values.end_date.format("YYYY-MM-DD"),
      };
      await api.post("/tournaments", payload);
      message.success("Tournament created");
      setIsTournamentModalOpen(false);
      tournamentForm.resetFields();
      loadTournaments();
    } catch {
      message.error("Cannot create tournament");
    }
  };

  const createMatch = async (values) => {
    try {
      const payload = {
        ...values,
        tournament_id: selectedTournament,
        start_time: values.start_time.format("YYYY-MM-DD HH:mm:ss"),
        end_time: values.end_time ? values.end_time.format("YYYY-MM-DD HH:mm:ss") : null,
      };
      await api.post("/matches", payload);
      message.success("Match created");
      setIsMatchModalOpen(false);
      matchForm.resetFields();
      loadMatches(selectedTournament);
    } catch {
      message.error("Cannot create match");
    }
  };

  const createTeam = async (values) => {
    try {
      await api.post("/teams", values);
      message.success("Team created");
      setTeamModalOpen(false);
      teamForm.resetFields();
      loadTeams();
    } catch {
      message.error("Failed to create team");
    }
  };

  const createPlayer = async (values) => {
    if (!selectedTeamForPlayer) return message.error("Team not selected");

    try {
      const res = await api.post("/players", values);
      const newPlayer = res.data.data;
      message.success("Player created");

      // Assign to team
      await api.post("/team-players", {
        team_id: selectedTeamForPlayer,
        player_id: newPlayer.player_id,
        is_playing: true,
        shirt_number: null,
      });

      message.success("Player added to team");
      setPlayerModalOpen(false);
      playerForm.resetFields();
      loadPlayers();
      loadTeamPlayers(selectedTeamForPlayer);
    } catch {
      message.error("Failed to create player");
    }
  };

  // =================== ASSIGN TEAMS TO MATCH ===================
  const assignTeamsToMatch = async () => {
    if (!teamA && !teamB) return message.error("Please select at least one team");

    try {
      if (teamA) await api.post("/matchteams", { match_id: selectedMatch, team_id: teamA, role: "team1" });
      if (teamB) await api.post("/matchteams", { match_id: selectedMatch, team_id: teamB, role: "team2" });

      message.success("Teams assigned successfully");
    } catch {
      message.error("Failed to assign teams");
    }
  };

  // =================== EFFECTS ===================
  // Load players for selected team
  useEffect(() => { if (teamA) loadTeamPlayers(teamA); }, [teamA]);
  useEffect(() => { if (teamB) loadTeamPlayers(teamB); }, [teamB]);

  // =================== RENDER FUNCTIONS ===================
const renderTeamCard = (teamValue, setTeamValue) => {
  const playersForThisTeam = teamPlayersMap[teamValue] || [];

  return (
    <Card size="small" bordered className="shadow-sm">
      <h3 className="font-semibold mb-3">{teamValue === teamA ? "Team A" : "Team B"}</h3>
      <Select
        className="w-full"
        placeholder="Choose Team"
        value={teamValue}
        onChange={setTeamValue}
      >
        {teams.map((team) => (
          <Select.Option key={team.team_id} value={team.team_id}>
            {team.name}
          </Select.Option>
        ))}
      </Select>

      <Button className="mt-4 w-full" type="primary" onClick={() => setTeamModalOpen(true)}>
        + Create Team
      </Button>

      <div className="mt-4">
        

        {/* ===== Add Existing Player ===== */}
        <h4 className="font-medium mt-4 mb-2">Add Existing Player</h4>
        {/*players
          .filter(p => !playersForThisTeam.some(tp => tp.player_id === p.player_id))
          .map(player => (
            <div key={player.player_id} className="flex justify-between items-center mb-1">
              <span>{player.name} ({player.role})</span>
              <Button
                size="small"
                type="primary"
                onClick={async () => {
                  try {
                    await api.post("/team-players", {
                      team_id: teamValue,
                      player_id: player.player_id,
                      is_playing: true,
                      shirt_number: null,
                    });
                    message.success("Player added to team");
                    loadTeamPlayers(teamValue); // refresh only this team's players
                  } catch {
                    message.error("Failed to add player");
                  }
                }}
              >
                + Add
              </Button>
            </div>
          ))*/}

        {/* ===== Multi-select Players ===== */}
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder="Select Players for this Team"
          value={playersForThisTeam.map(tp => tp.player_id)}
          onChange={async (selectedPlayerIds) => {
            try {
              // Delete existing
              for (let tp of playersForThisTeam) {
                await api.delete(`/team-players/${tp.id}`);
              }
              // Add new
              for (let playerId of selectedPlayerIds) {
                await api.post("/team-players", { team_id: teamValue, player_id: playerId, is_playing: true, shirt_number: null });
              }
              message.success("Team players updated");
              loadTeamPlayers(teamValue);
            } catch {
              message.error("Failed to update team players");
            }
          }}
        >
          {players.map(player => (
            <Select.Option key={player.player_id} value={player.player_id}>
              {player.name} ({player.role})
            </Select.Option>
          ))}
        </Select>

        <Button
          className="mt-2 w-full"
          type="primary"
          onClick={() => { setSelectedTeamForPlayer(teamValue); setPlayerModalOpen(true); }}
        >
          + Add New Player
        </Button>

        <h4 className="font-medium mb-2">Players in Team</h4>
        {playersForThisTeam.map(tp => (
          <div key={tp.id} className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              {tp.Player?.profile_pic && <img src={tp.Player.profile_pic} alt="" className="w-6 h-6 rounded-full" />}
              <span>{tp.Player?.name} ({tp.Player?.role})</span>
            </div>
            <span>#{tp.shirt_number || "-"}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

const handleStartMatch = () => {
  if (!teamA || !teamB) {
    message.warning("At least 2 teams are required to start the match");
    return;
  }

  if (!selectedMatch) {
    message.warning("Please select a match first");
    return;
  }

  navigate(`/dashboard/matches/${selectedMatch}/live`);
};

return (
  <div className="p-6 bg-gray-100 min-h-screen">
    <h1 className="text-3xl font-bold text-gray-800 mb-8">Tournament Setups</h1>

    {/* STEP 1: Tournament */}
    <Card className="mb-8 shadow-lg rounded-lg bg-white p-6">
      <h2 className="text-xl font-semibold text-gray-700 mb-4">Select Tournament</h2>
      <div className="flex flex-col md:flex-row gap-4 md:items-center">
        <Select
          placeholder="Select Tournament"
          style={{ width: 300 }}
          value={selectedTournament}
          onChange={(value) => { setSelectedTournament(value); setSelectedMatch(null); loadMatches(value); }}
          className="w-full md:w-72"
        >
          {tournaments.map((t) => (
            <Select.Option key={t.tournament_id} value={t.tournament_id}>
              {t.name}
            </Select.Option>
          ))}
        </Select>
        <Button type="primary" className="bg-blue-600 hover:bg-blue-700 transition-all" onClick={() => setIsTournamentModalOpen(true)}>
          + Create Tournament
        </Button>
      </div>
    </Card>

    {/* STEP 2: Match */}
    {selectedTournament && (
      <Card className="mb-8 shadow-lg rounded-lg bg-white p-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">Select Match</h2>
        {loadingMatches ? (
          <p className="text-gray-500">Loading matches...</p>
        ) : matches.length === 0 ? (
          <div className="text-gray-500 p-3 bg-gray-50 rounded">No matches found for this tournament.</div>
        ) : (
          <Select
            placeholder="Select Match"
            value={selectedMatch}
            onChange={async (value) => {
              setSelectedMatch(value);
              setSelectedTeamForPlayer(null);
              await loadTeams();
              setTimeout(() => loadAssignedTeams(value), 150);
            }}
            className="w-full md:w-96"
          >
            {matches.map((m) => (
              <Select.Option key={m.match_id} value={m.match_id}>{m.name}</Select.Option>
            ))}
          </Select>
        )}
        <div className="mt-4 flex flex-col md:flex-row gap-3 md:items-center">
          <Button type="default" onClick={() => setIsMatchModalOpen(true)}>+ Create Match</Button>
          {selectedMatch && <span className="text-green-600 font-medium">Selected Match ID: {selectedMatch}</span>}
        </div>
      </Card>
    )}

    {/* STEP 3: Teams */}
    {selectedMatch && (
      <Card className="mb-8 shadow-lg rounded-lg bg-white p-6">
        <h2 className="text-xl font-semibold text-gray-700 mb-6">Select Teams</h2>
        <div className="flex flex-col md:flex-row gap-6 mb-6">
          <div className="flex-1">{renderTeamCard(teamA, setTeamA)}</div>
          <div className="flex-1">{renderTeamCard(teamB, setTeamB)}</div>
        </div>
        <Button
          type="primary"
          className="bg-green-600 hover:bg-green-700 transition-all"
          onClick={handleStartMatch}
        >
          Start Match
        </Button>
      </Card>
    )}

    {/* MODALS */}
    <Modal
      title="Create Tournament"
      open={isTournamentModalOpen}
      onCancel={() => setIsTournamentModalOpen(false)}
      onOk={() => tournamentForm.submit()}
      className="rounded-lg"
      bodyStyle={{ padding: 20 }}
    >
      <Form form={tournamentForm} layout="vertical" onFinish={createTournament}>
        <Form.Item name="name" label="Tournament Name" rules={[{ required: true }]}>
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="sport" label="Sport" rules={[{ required: true }]}>
          <Select className="rounded-md border-gray-300 focus:ring focus:ring-blue-300">
            <Select.Option value="cricket">Cricket</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="category" label="Category">
          <Select className="rounded-md border-gray-300 focus:ring focus:ring-blue-300">
            <Select.Option value="domestic">Domestic</Select.Option>
            <Select.Option value="international">International</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}>
          <DatePicker className="w-full rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="end_date" label="End Date" rules={[{ required: true }]}>
          <DatePicker className="w-full rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
      </Form>
    </Modal>

    {/* Create Match Modal */}
    <Modal
      title="Create Match"
      open={isMatchModalOpen}
      onCancel={() => setIsMatchModalOpen(false)}
      onOk={() => matchForm.submit()}
      className="rounded-lg"
      bodyStyle={{ padding: 20 }}
    >
      <Form form={matchForm} layout="vertical" onFinish={createMatch}>
        <Form.Item name="name" label="Match Name" rules={[{ required: true }]}>
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="start_time" label="Start Time" rules={[{ required: true }]}>
          <DatePicker showTime className="w-full rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="end_time" label="End Time">
          <DatePicker showTime className="w-full rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="venue" label="Venue">
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="overs" label="Overs">
          <Input type="number" className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="youtube_link" label="YouTube Link">
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="poster" label="Poster URL">
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="match_type" label="Match Type">
          <Select className="rounded-md border-gray-300 focus:ring focus:ring-blue-300">
            <Select.Option value="international">International</Select.Option>
            <Select.Option value="domestic">Domestic</Select.Option>
            <Select.Option value="local">Local</Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>

    {/* Team Modal */}
    <Modal title="Create Team" open={teamModalOpen} onCancel={() => setTeamModalOpen(false)} onOk={() => teamForm.submit()} className="rounded-lg">
      <Form form={teamForm} layout="vertical" onFinish={createTeam}>
        <Form.Item name="name" label="Team Name" rules={[{ required: true }]}>
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="captain_id" label="Captain Player ID">
          <Input type="number" className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="profile_pic" label="Profile Pic URL">
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
      </Form>
    </Modal>

    {/* Player Modal */}
    <Modal title="Create Player" open={playerModalOpen} onCancel={() => setPlayerModalOpen(false)} onOk={() => playerForm.submit()} className="rounded-lg">
      <Form form={playerForm} layout="vertical" onFinish={createPlayer}>
        <Form.Item name="name" label="Player Name" rules={[{ required: true }]}>
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="role" label="Role" rules={[{ required: true }]}>
          <Select className="rounded-md border-gray-300 focus:ring focus:ring-blue-300">
            <Select.Option value="batsman">Batsman</Select.Option>
            <Select.Option value="bowler">Bowler</Select.Option>
            <Select.Option value="allrounder">Allrounder</Select.Option>
            <Select.Option value="wicketkeeper">Wicketkeeper</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="country" label="Country">
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
        <Form.Item name="profile_pic" label="Profile Pic URL">
          <Input className="rounded-md border-gray-300 focus:ring focus:ring-blue-300" />
        </Form.Item>
      </Form>
    </Modal>
  </div>
);

}
